import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { Link } from 'react-router-dom';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import InsightsIcon from '@mui/icons-material/Insights';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import useMediaQuery from '@mui/material/useMediaQuery';

import weakAlert from 'assets/images/illustrations/Weak-alert-example.png';
import decisionTree from 'assets/images/illustrations/decision-tree.png';

const steps = [
    {
        icon: <CloudDownloadIcon sx={{ fontSize: 56 }} />,
        step: '01',
        title: 'Collect',
        description:
            'Continuously collect sign-in logs, Microsoft Graph data, and resource activity from your Microsoft 365 tenant — as frequently as every minute. Every event enters the pipeline. Nothing is filtered or dropped.',
    },
    {
        icon: <InsightsIcon sx={{ fontSize: 56 }} />,
        step: '02',
        title: 'Analyze',
        description:
            'Proprietary AI algorithms build unique behavioral profiles for each user and tenant. Usage patterns, geographic data, access timing, and file activity are all used to establish what "normal" looks like — and to flag when it changes.',
    },
    {
        icon: <NotificationImportantIcon sx={{ fontSize: 56 }} />,
        step: '03',
        title: 'Notify',
        description:
            'The moment anomalous activity matches the pattern of an attack, account compromise, or data exfiltration, an alert is generated. In under 1 minute. No analyst required. No delay.',
    },
];

export default function HowItWorks() {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

    return (
        <>
            {/* ─── 3-STEP PROCESS ─── */}
            <Box
                id="howitworks"
                width="100%"
                sx={{
                    background: '#ffffff',
                    display: 'grid',
                    placeItems: 'center',
                    py: 10,
                }}
            >
                <Grid container maxWidth="lg" spacing={2} justifyContent="center" sx={{ px: 2 }}>
                    <Grid item size="12" textAlign="center" mb={6}>
                        <Typography
                            variant="overline"
                            color="prevailRed.main"
                            fontWeight="bold"
                            sx={{ letterSpacing: 3, fontSize: '0.9em' }}
                        >
                            HOW IT WORKS
                        </Typography>
                        <Typography variant="h3" color="grey.800" fontWeight="bold" mt={1}>
                            Always On. Always Watching.
                        </Typography>
                        <Typography variant="h6" color="grey.600" mt={2} sx={{ maxWidth: '600px', mx: 'auto' }}>
                            PREVAIL connects to your Microsoft 365 tenant in minutes and immediately
                            starts building behavioral intelligence — no configuration required.
                        </Typography>
                    </Grid>

                    {steps.map(({ icon, step, title, description }) => (
                        <Grid item xs={12} md={4} key={step} sx={{ display: 'flex' }}>
                            <Box
                                sx={{
                                    border: '2px solid',
                                    borderColor: 'grey.200',
                                    borderRadius: 2,
                                    p: 4,
                                    position: 'relative',
                                    transition: 'border-color 0.3s',
                                    '&:hover': { borderColor: '#d90000' },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%',
                                }}
                            >
                                <Typography
                                    sx={{
                                        position: 'absolute',
                                        top: 12,
                                        right: 18,
                                        fontSize: '3.5em',
                                        fontWeight: 900,
                                        color: 'rgba(217,0,0,0.07)',
                                        lineHeight: 1,
                                        userSelect: 'none',
                                    }}
                                >
                                    {step}
                                </Typography>
                                <Typography color="primary.red" mb={2}>{icon}</Typography>
                                <Typography variant="h5" color="grey.800" fontWeight="bold" mb={2}>
                                    {title}
                                </Typography>
                                <Typography variant="body2" color="grey.600" lineHeight={1.8} sx={{ fontSize: '1em' }}>
                                    {description}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* ─── REAL-TIME DETECTION DETAIL ─── */}
            <Box
                width="100%"
                sx={{
                    background: '#f8f9fa',
                    display: 'grid',
                    placeItems: 'center',
                    py: 10,
                }}
            >
                <Grid container maxWidth="lg" spacing={2} justifyContent="center" sx={{ px: 2 }} mb={4}>
                    <Grid item xs={12} textAlign="center">
                        <Typography variant="h3" color="grey.800" fontWeight="bold">
                            Real-Time Threat Detection for Microsoft 365
                        </Typography>
                        <Typography variant="h6" color="grey.600" mt={2} sx={{ maxWidth: '650px', mx: 'auto' }}>
                            Rule-based security is no match for modern attacks. PREVAIL adapts.
                        </Typography>
                    </Grid>
                </Grid>

                {/* Row 1: text left, image right */}
                <Grid container maxWidth="xl" spacing={6} justifyContent="center" alignItems="center"
                    sx={{ px: 2 }} mb={8}>
                    <Grid size={{ xs: 12, md: 5}}>
                        <Typography variant="body1" color="grey.700" mb={3} sx={{ fontSize: '1.15em', lineHeight: 1.8 }}>
                            Traditional Microsoft 365 security relies on simplistic rules; blocking
                            logins from unusual locations or flagging "impossible travel." Today's
                            attackers know how to bypass those checks entirely.
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Box
                                component="img"
                                src={weakAlert}
                                alt="Microsoft 365 rule-based alert showing MFA satisfied despite impossible travel and blacklisted IP — illustrating insufficient detection"
                                sx={{ width: '100%', height: 'auto', display: 'block' }}
                            />
                                                    <Typography variant="caption" color="grey.500" sx={{ display: 'block', mt: 1, fontStyle: 'italic', textAlign: 'center' }}>
                            Rule-based detection flags "Impossible Travel" but marks MFA as satisfied — leaving the breach unblocked.
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 5 }}>
                         <Box
                                component="img"
                                src={decisionTree}
                                alt="PREVAIL behavioral detection flow — showing how anomalous logins, data access, and privilege changes are connected to identify account compromise"
                                sx={{ width: '100%', height: 'auto', display: 'block' }}
                            />
                            <Typography variant="caption" color="grey.500" sx={{ display: 'block', mt: 1, fontStyle: 'italic', textAlign: 'center' }}>
                            PREVAIL correlates multiple behavioral signals to identify account exploitation and privilege abuse.
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 5}}>
                        <Typography variant="body1" color="grey.700" sx={{ fontSize: '1.15em', lineHeight: 1.8 }}>
                            PREVAIL goes beyond rules by using advanced behavioral analytics and
                            predictive AI to model how users normally work, so you get alerts before
                            unauthorized access spreads, not after the damage is done.
                        </Typography>
                    </Grid>
                </Grid>

                <Divider sx={{ width: '100%', maxWidth: 'lg', borderColor: 'grey.200', mb: 8 }} />

                {/* Row 2: image left, text right */}
                <Grid container maxWidth="lg" spacing={6} justifyContent="center" alignItems="center"
                    sx={{ px: 2 }}>
                   
                    <Grid item xs={12} md={5} order={{ xs: 1, md: 2 }}>
                        <Typography variant="body1" color="grey.700" mb={3} sx={{ fontSize: '1.15em', lineHeight: 1.8 }}>
                            Whether you're a small business protecting your own data or an MSP
                            supporting multiple clients, PREVAIL accurately identifies account
                            takeovers, insider threats, and compromised credentials — not just based
                            on where someone logs in, but on how they behave once they're in.
                        </Typography>
                        <Typography variant="body1" color="grey.700" mb={5} sx={{ fontSize: '1.15em', lineHeight: 1.8 }}>
                            In as little as one minute after an attack begins, you get actionable
                            alerts, fewer false positives, and peace of mind — without needing a
                            full-time security team.
                        </Typography>
                        <Link to="/products/ms365">
                            <Button
                                variant="contained"
                                color="prevailRed"
                                size="large"
                                sx={{ minWidth: isMobile ? '100%' : 0, pl: 5, pr: 5 }}
                            >
                                <Typography sx={{ fontSize: '1.1em' }} color="grey.100">
                                    Learn More
                                </Typography>
                            </Button>
                        </Link>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
}
